import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import {
    Image as ImageIcon,
    PictureAsPdf as PdfIcon,
    CloudQueue as QueuedIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import safetyScheduleService, { MAX_FILES_PER_VISIT } from '../../../services/safetyScheduleService';
import PdfPreviewModal from '../../jha/pdf/PdfPreviewModal';
import { dayTitle } from './visitUtils';

function isPdf(file) {
    return (file.mimeType || '').includes('pdf')
        || (file.dataUrl || '').startsWith('data:application/pdf')
        || /\.pdf$/i.test(file.name || '');
}

/** Имя с расширением: окно просмотра по нему решает, картинка это или PDF. */
function fileName(file) {
    if (file.name) return file.name;
    return isPdf(file) ? 'Document.pdf' : 'Photo.jpg';
}

/** Время файла по Каролине — сервер отдаёт ISO с оффсетом. */
function fileTime(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-US', {
        timeZone: 'America/New_York', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit',
    });
}

/**
 * Файлы визита под скрепкой: каждый снимок и документ отдельно, открыть и
 * посмотреть. То, что ещё не отправлено, лежит на айпаде и видно сразу — с
 * пометкой «Waiting to send». Уже отправленное скачивается с сервера, поэтому
 * без связи вместо снимка — значок.
 */
const VisitFilesDialog = ({ open, assignment, online, onClose }) => {
    const [queued, setQueued] = useState([]);
    const [thumbs, setThumbs] = useState({});
    const [preview, setPreview] = useState({ open: false, blob: null, name: '', loading: false });

    const visit = useMemo(() => assignment?.visit || {}, [assignment]);
    const serverFiles = useMemo(() => (visit.files || []).filter(Boolean), [visit]);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        safetyScheduleService.getQueuedFiles(visit.uuid).then((list) => {
            if (!cancelled) setQueued(list);
        });
        return () => { cancelled = true; };
    }, [open, visit.uuid]);

    // Миниатюры отправленных снимков: файл отдаётся только с токеном, поэтому
    // скачиваем его сами, а не ставим адрес в <img>.
    useEffect(() => {
        if (!open || !online) return undefined;
        let cancelled = false;
        const urls = [];

        (async () => {
            for (const file of serverFiles.filter((f) => !isPdf(f))) {
                try {
                    const blob = await safetyScheduleService.loadFileBlob(file);
                    if (cancelled) return;
                    const url = URL.createObjectURL(blob);
                    urls.push(url);
                    setThumbs((prev) => ({ ...prev, [file.id]: url }));
                } catch (e) {
                    // Не скачался — останется значок, открыть можно будет по нажатию.
                }
            }
        })();

        return () => {
            cancelled = true;
            urls.forEach((url) => URL.revokeObjectURL(url));
            setThumbs({});
        };
    }, [open, online, serverFiles]);

    const files = [
        ...serverFiles.map((f) => ({ ...f, key: `s-${f.id}` })),
        ...queued.map((f) => ({ ...f, key: `q-${f.opId}`, queued: true })),
    ];
    const photos = files.filter((f) => !isPdf(f));
    const documents = files.filter((f) => isPdf(f));

    const view = async (file) => {
        if (!file.dataUrl && !online) {
            Swal.fire('No connection', 'Connect to the internet to open this file.', 'info');
            return;
        }

        const name = fileName(file);
        setPreview({ open: true, blob: null, name, loading: true });
        try {
            const blob = await safetyScheduleService.loadFileBlob(file);
            setPreview({ open: true, blob, name, loading: false });
        } catch (e) {
            setPreview({ open: false, blob: null, name: '', loading: false });
            Swal.fire('Could not open the file', e?.response?.data?.message || e.message || 'Please try again', 'error');
        }
    };

    const photoSrc = (file) => file.dataUrl || thumbs[file.id] || null;

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ pb: 0.5 }}>
                    Files
                    <Typography variant="body2" color="text.secondary">
                        {assignment?.location}
                        {assignment?.workDate ? ` · ${dayTitle(assignment.workDate)}` : ''}
                        {` · ${files.length} of ${MAX_FILES_PER_VISIT}`}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {files.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            Nothing attached yet.
                        </Typography>
                    )}

                    {photos.length > 0 && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 1.5, mt: 1 }}>
                            {photos.map((file) => (
                                <Box
                                    key={file.key}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => view(file)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') view(file); }}
                                    sx={{ cursor: 'pointer', minWidth: 0 }}
                                >
                                    <Box sx={{
                                        position: 'relative', width: '100%', aspectRatio: '1 / 1', borderRadius: 1,
                                        border: '1px solid #ddd', overflow: 'hidden', bgcolor: '#f5f5f5',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        {photoSrc(file)
                                            ? <Box component="img" src={photoSrc(file)} alt={fileName(file)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            : <ImageIcon sx={{ color: '#9e9e9e', fontSize: 36 }} />}
                                        {file.queued && (
                                            <Chip
                                                icon={<QueuedIcon sx={{ color: 'white !important', fontSize: 14 }} />}
                                                label="Waiting to send"
                                                size="small"
                                                sx={{ position: 'absolute', left: 4, bottom: 4, height: 20, fontSize: '0.65rem', bgcolor: '#ff9800', color: 'white' }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
                                        {fileTime(file.createdAt) || fileName(file)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {documents.length > 0 && (
                        <List dense sx={{ mt: photos.length ? 1.5 : 0 }}>
                            {documents.map((file) => (
                                <ListItemButton key={file.key} onClick={() => view(file)} sx={{ borderRadius: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <PdfIcon sx={{ color: '#d32f2f' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={fileName(file)}
                                        secondary={file.queued ? 'Waiting to send' : fileTime(file.createdAt)}
                                        primaryTypographyProps={{ noWrap: true }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            <PdfPreviewModal
                open={preview.open}
                onClose={() => setPreview({ open: false, blob: null, name: '', loading: false })}
                pdfBlob={preview.blob}
                filename={preview.name}
                loading={preview.loading}
            />
        </>
    );
};

export default VisitFilesDialog;
